/**
 * Schema factories for sharing/ownership.
 *
 * Templates make a resource table "ownable" by spreading `ownableColumns()` into
 * the table definition and declaring a companion shares table via
 * `createSharesTable()`. The shares table carries per-principal grants; the
 * parent table's `visibility` column is the coarse default (private / org /
 * public).
 *
 *   import { table, text, ownableColumns, createSharesTable } from
 *     "@agent-native/core/db/schema";
 *
 *   export const decks = table("decks", {
 *     id: text("id").primaryKey(),
 *     title: text("title").notNull(),
 *     data: text("data").notNull(),
 *     ...ownableColumns(),
 *   });
 *
 *   export const deckShares = createSharesTable("deck_shares");
 *
 * The share row `role` supports `viewer | editor | admin`. `admin` is a
 * share-level capability — it lets non-owners manage shares. The single
 * `owner_email` column on the resource remains the ground truth for ownership.
 */

import { table, text, now } from "../db/schema.js";

/**
 * Columns any ownable resource should include. Spread into the table definition.
 *
 * - `ownerEmail` — the creator. Auto-filled by write actions from the request context.
 * - `orgId`     — owner's active org at creation time. Used for `visibility = 'org'` checks.
 * - `visibility`— coarse default: `'private' | 'org' | 'public'`. Default `'private'`.
 */
export function ownableColumns() {
  return {
    ownerEmail: text("owner_email").notNull().default("local@localhost"),
    orgId: text("org_id"),
    visibility: text("visibility", {
      enum: ["private", "org", "public"],
    })
      .notNull()
      .default("private"),
  };
}

/**
 * Create a companion shares table for an ownable resource. Call this right
 * after defining the parent table.
 *
 * Columns:
 * - `id`              — share row id (generated by the share action)
 * - `resourceId`      — id of the parent resource
 * - `principalType`   — `'user' | 'org'`
 * - `principalId`     — email (for user) or org id (for org)
 * - `role`            — `'viewer' | 'editor' | 'admin'`
 * - `createdBy`       — email of the user who created the share
 * - `createdAt`       — ISO timestamp
 *
 * Uniqueness across `(resource_id, principal_type, principal_id)` is enforced
 * at the action layer via upsert-style logic. A composite DB-level unique
 * index can be added per template if desired — omitted here to keep the
 * factory dialect-agnostic.
 */
export function createSharesTable(tableName: string) {
  return table(tableName, {
    id: text("id").primaryKey(),
    resourceId: text("resource_id").notNull(),
    principalType: text("principal_type", {
      enum: ["user", "org"],
    }).notNull(),
    principalId: text("principal_id").notNull(),
    role: text("role", { enum: ["viewer", "editor", "admin"] })
      .notNull()
      .default("viewer"),
    createdBy: text("created_by").notNull(),
    createdAt: text("created_at").notNull().default(now()),
  });
}

export type Visibility = "private" | "org" | "public";
export type ShareRole = "viewer" | "editor" | "admin";
export type PrincipalType = "user" | "org";

/** Role precedence — higher number = stronger capability. */
export const ROLE_RANK: Record<ShareRole | "owner", number> = {
  viewer: 1,
  editor: 2,
  admin: 3,
  owner: 4,
};

export function roleSatisfies(
  actual: ShareRole | "owner",
  minimum: ShareRole | "owner",
): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[minimum];
}
